import { useFloating, offset, flip, size, autoUpdate } from '@floating-ui/react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import dropdownStyles from './Dropdown.module.scss.js';
import { NoData } from './NoData.js';
import styles from '../Inputs.module.scss.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useA11y } from '../../provider/A11yProvider.js';
import { UNSAFE_Icon } from '../../icon/Icon.js';
import { Dialog } from '../../dialog/Dialog.js';
import classNames from 'clsx';
import { MdSearch, MdKeyboardArrowDown } from '../../node_modules/react-icons/md/index.js';
import { DsxLoadingSpinner } from '../../icon/icons/loading-spinner.js';

function Dropdown(props) {
  const {
    activeId,
    children,
    className: callerClassName,
    closePopover,
    disabled,
    id,
    listId,
    loadingState,
    name,
    open,
    placeholder,
    search,
    searchRef,
    selectedLabel,
    tag,
    tagKey,
    value,
    variant = 'default',
    onBlur,
    onDropdownClick,
    onFocus,
    onSearch,
    onSearchInput,
    onSearchKeyDown
  } = props;
  const containerClassName = classNames('dsx-component', styles['input-box'], styles['container'], styles['button-first'], styles['arrow-input'], styles['cursor-text'], styles[variant], callerClassName);
  const searchInputClassName = classNames(styles['input']);
  const popoverClassName = classNames('dsx-component', styles['popover'], styles['list']);
  const {
    portalTarget
  } = useA11y();
  const {
    floatingStyles,
    refs,
    elements,
    update
  } = useFloating({
    open,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(8), flip({
      crossAxis: false
    }), size({
      apply(_ref) {
        let {
          rects,
          elements
        } = _ref;
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`
        });
      }
    })]
  });
  useEffect(() => {
    if (open && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [open, elements, update]);
  function handleBlur() {
    if (open) {
      closePopover();
    }
    onBlur?.();
  }
  function preventBlur(event) {
    event.preventDefault();
  }
  const contentLabel = selectedLabel ?? placeholder;
  const contentLabelClassName = classNames(dropdownStyles['content-label'], (open || selectedLabel == null) && dropdownStyles['placeholder']);
  const icon = loadingState === 'loading' ? DsxLoadingSpinner : MdKeyboardArrowDown;
  return /*#__PURE__*/jsxs("div", {
    className: styles['arrow-wrapper'],
    children: [/*#__PURE__*/jsxs("fieldset", {
      className: containerClassName,
      disabled: disabled,
      ref: refs.setReference,
      onClick: onDropdownClick,
      onMouseDown: preventBlur,
      [tagKey]: tag,
      children: [/*#__PURE__*/jsx("input", {
        name: name,
        type: "hidden",
        value: value
      }), /*#__PURE__*/jsx(UNSAFE_Icon, {
        icon: MdSearch,
        className: styles['icon']
      }), /*#__PURE__*/jsxs("div", {
        className: dropdownStyles['content-label-wrapper'],
        children: [!search && /*#__PURE__*/jsx("div", {
          className: contentLabelClassName,
          children: contentLabel
        }), /*#__PURE__*/jsx("input", {
          "aria-activedescendant": activeId,
          "aria-autocomplete": "list",
          "aria-controls": listId,
          "aria-expanded": open,
          "aria-haspopup": "listbox",
          autoCorrect: "off",
          className: searchInputClassName,
          id: id,
          type: "text",
          ref: searchRef,
          role: "combobox",
          value: search,
          onBlur: handleBlur,
          onFocus: onFocus,
          onInput: onSearchInput,
          onKeyDown: onSearchKeyDown,
          [`${tagKey}--search`]: tag
        })]
      }), /*#__PURE__*/jsx("div", {
        "aria-hidden": true,
        className: styles['arrow'],
        children: /*#__PURE__*/jsx(UNSAFE_Icon, {
          icon: icon
        })
      })]
    }), portalTarget && /*#__PURE__*/createPortal(/*#__PURE__*/jsx(Dialog, {
      focusOnOpen: false,
      isOpen: open,
      className: popoverClassName,
      ref: refs.setFloating,
      style: floatingStyles,
      onMouseDown: preventBlur,
      [`${tagKey}--popover`]: tag,
      children: /*#__PURE__*/jsx("div", {
        children: children || /*#__PURE__*/jsx(NoData, {
          loadingState: loadingState,
          search: search,
          onSearch: onSearch
        })
      })
    }), portalTarget)]
  });
}

export { Dropdown };
